import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { View } from 'foundations-components/components/View';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { DashboardPage } from 'foundations-components/transitional/components/DashboardPage';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { CollectionCard as DSCollectionCard } from 'marketplace-discovery-studio-lib/components/CollectionCard';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { UsageScreenProvider, useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getPageViewedEvent, getSearchResultClickedEvent, SearchResultViewTracker } from 'marketplace-ui-tracking/events/marketplace';
import { useCallback, useEffect, useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import { GET_ALL_PUBLISHED_COLLECTIONS_QUERY as GET_ALL_PUBLISHED_COLLECTIONS_QUERY_PUBLIC } from '../../__generated__/chirp/com/hubspot/marketplace/quality/rpc/SelfServePublicRpc.dfc.new';
import { GET_ALL_PUBLISHED_COLLECTIONS_QUERY } from '../../__generated__/chirp/com/hubspot/marketplace/quality/rpc/SelfServeRpc.dfc.new';
import { UmbrellaCollectionCard } from '../collections/components/UmbrellaCollectionCard';
import { adaptDSPreviewItems, getCollectionUrl, useDiscoveryCollections } from '../collections/hooks/useDiscoveryCollections';
import { StorefrontDashboardWithTabs } from '../components/StorefrontDashboardWithTabs';
import { StorefrontPageMetadata } from '../components/StorefrontPageMetadata';
import { UnifiedHeader } from '../components/headers/UnifiedHeader';
import { Screens } from '../constants/usage';
import { useGates } from '../hooks/useGates';
import { useAppContextQuery } from '../hooks/useAppContextQuery';
import { FeaturedCollectionCard } from './components/FeaturedCollectionCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function FeaturedPage() {
  return /*#__PURE__*/_jsx(UsageScreenProvider, {
    screen: Screens.FEATURED,
    children: /*#__PURE__*/_jsx(FeaturedPageContent, {})
  });
}
export function FeaturedPageContent() {
  var _data$result2;
  const gates = useGates();
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const {
    isMobile
  } = useResponsiveProps({
    isMobile: {
      xs: true,
      lg: false
    }
  });
  const dsResult = useDiscoveryCollections({
    skip: !gates.newDashboard
  });
  const {
    data,
    loading: legacyLoading,
    error: legacyError
  } = useAppContextQuery({
    app: GET_ALL_PUBLISHED_COLLECTIONS_QUERY,
    public: GET_ALL_PUBLISHED_COLLECTIONS_QUERY_PUBLIC,
    options: {
      skip: gates.newDashboard
    }
  });
  const loading = gates.newDashboard ? dsResult.loading : legacyLoading;
  const error = gates.newDashboard ? dsResult.error : legacyError;
  const collections = useMemo(() => {
    var _data$result;
    return (data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 || (_data$result = _data$result.collections) === null || _data$result === void 0 ? void 0 : _data$result.filter(collection => collection.displayed !== false).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))) || [];
  }, [data === null || data === void 0 || (_data$result2 = data.result) === null || _data$result2 === void 0 ? void 0 : _data$result2.collections]);
  const trackCollectionClicked = useCallback((id, slug, index, resultCount) => {
    trackMarketplaceEvent(getSearchResultClickedEvent({
      listingId: id,
      resultCount,
      resultIndex: index,
      resultType: 'COLLECTION',
      slug
    }));
  }, [trackMarketplaceEvent]);
  useEffect(() => {
    trackMarketplaceEvent(getPageViewedEvent({}));
  }, [trackMarketplaceEvent]);
  const renderDSCollectionCard = (collection, index, resultCount) => /*#__PURE__*/_jsx(SearchResultViewTracker, {
    category: collection.slug,
    collectionId: collection.entityId,
    listingId: collection.entityId,
    resultCount: resultCount,
    resultIndex: index,
    resultType: "COLLECTION",
    slug: collection.slug,
    children: /*#__PURE__*/_jsx(DSCollectionCard, {
      title: collection.name,
      description: collection.description ? /*#__PURE__*/_jsx(SanitizedText, {
        body: collection.description,
        WrapperComponent: "span"
      }) : undefined,
      buttonText: unescapedText('marketplaceStorefront.collectionCard.viewCollection'),
      buttonUrl: getCollectionUrl(collection.slug),
      previewItems: adaptDSPreviewItems(collection.previewItems),
      listingCount: collection.listingCount,
      onClick: () => trackCollectionClicked(collection.entityId, collection.slug, index, resultCount)
    })
  }, collection.slug);
  const dsContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(CardGrid, {
      columns: isMobile ? 1 : 2,
      gap: "400",
      children: dsResult.collections.slice(0, 2).map((collection, index) => renderDSCollectionCard(collection, index, dsResult.collections.length))
    }), /*#__PURE__*/_jsx(View, {
      marginBlock: "400",
      children: /*#__PURE__*/_jsx(UmbrellaCollectionCard, {
        title: unescapedText('marketplaceStorefront.umbrellaCollections.aiEssentials.title'),
        description: unescapedText(gates.workflowTemplatesPhase2 ? 'marketplaceStorefront.umbrellaCollections.aiEssentials.descriptionRebranded' : 'marketplaceStorefront.umbrellaCollections.aiEssentials.description'),
        slug: "ai-essentials"
      })
    }), /*#__PURE__*/_jsx(CardGrid, {
      columns: isMobile ? 1 : 2,
      gap: "400",
      children: dsResult.collections.slice(2).map((collection, index) => renderDSCollectionCard(collection, index + 2, dsResult.collections.length))
    })]
  });
  const content = loading ? /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true,
    size: "medium",
    marginBlockStart: "800"
  }) : error ? /*#__PURE__*/_jsx(ErrorMessage, {
    type: "badRequest"
  }) : gates.newDashboard ? dsContent : /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(CardGrid, {
      columns: isMobile ? 1 : 2,
      gap: "400",
      children: collections.slice(0, 2).map((collection, index) => /*#__PURE__*/_jsx(FeaturedCollectionCard, {
        collection: collection,
        index: index,
        resultCount: collections.length
      }, `${collection.slug}-${index}`))
    }), /*#__PURE__*/_jsx(View, {
      marginBlock: "400",
      children: /*#__PURE__*/_jsx(UmbrellaCollectionCard, {
        title: unescapedText('marketplaceStorefront.umbrellaCollections.aiEssentials.title'),
        description: unescapedText(gates.workflowTemplatesPhase2 ? 'marketplaceStorefront.umbrellaCollections.aiEssentials.descriptionRebranded' : 'marketplaceStorefront.umbrellaCollections.aiEssentials.description'),
        slug: "ai-essentials"
      })
    }), /*#__PURE__*/_jsx(CardGrid, {
      columns: isMobile ? 1 : 2,
      gap: "400",
      children: collections.slice(2).map((collection, index) => /*#__PURE__*/_jsx(FeaturedCollectionCard, {
        collection: collection,
        index: index,
        resultCount: collections.length
      }, `${collection.slug}-${index}`))
    })]
  });
  if (gates.workflowTemplatesPhase2) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [(gates.newDashboard ? dsResult.collections.length > 0 : !!data) && /*#__PURE__*/_jsx(NavMarker, {
        name: "FEATURED_LOAD"
      }), /*#__PURE__*/_jsx(StorefrontPageMetadata, {
        loading: loading,
        title: unescapedText('marketplaceStorefront.documentHead.featured.title'),
        metaDescription: unescapedText('marketplaceStorefront.documentHead.featured.metaDescription')
      }), /*#__PURE__*/_jsx(StorefrontDashboardWithTabs, {
        children: content
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!!data && /*#__PURE__*/_jsx(NavMarker, {
      name: "FEATURED_LOAD"
    }), /*#__PURE__*/_jsx(StorefrontPageMetadata, {
      loading: loading,
      title: unescapedText('marketplaceStorefront.documentHead.featured.title'),
      metaDescription: unescapedText('marketplaceStorefront.documentHead.featured.metaDescription')
    }), /*#__PURE__*/_jsx(DashboardPage, {
      headerComponent: /*#__PURE__*/_jsx(UnifiedHeader, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplaceStorefront.featured.title"
        }),
        pathKey: "FEATURED",
        dataTestId: "featured-page-header",
        isHomePage: false
      }),
      children: content
    })]
  });
}